import {
  FeedbackError,
  errorFromResponse,
  normalizeError
} from "./chunk-RNF7XJWR.js";

// src/core/fetcher.ts
function sameOrigin(url) {
  return typeof location === "undefined" || new URL(url, location.href).origin === location.origin;
}
function readCsrfCookie(name = "csrftoken") {
  if (typeof document === "undefined" || !document.cookie) return null;
  for (const pair of document.cookie.split(";")) {
    const [key, ...rest] = pair.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}
function parseJson(text) {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (e) {
    return { ok: false };
  }
}
function createFetchFetcher(options = {}) {
  const { headers: extraHeaders, csrf = "django-cookie", credentials = "same-origin" } = options;
  const readCsrf = csrf === "django-cookie" ? () => readCsrfCookie() : csrf;
  return async ({ url, method, body, signal }) => {
    var _a;
    const headers = new Headers(extraHeaders ? await extraHeaders() : void 0);
    headers.set("Accept", "application/json");
    if (body !== void 0) headers.set("Content-Type", "application/json");
    if (method !== "GET" && sameOrigin(url)) {
      const token = readCsrf();
      if (token) headers.set("X-CSRFToken", token);
    }
    const fetchImpl = (_a = options.fetch) != null ? _a : globalThis.fetch;
    let response;
    try {
      response = await fetchImpl(url, {
        method,
        credentials,
        headers,
        body: body === void 0 ? void 0 : JSON.stringify(body),
        signal
      });
    } catch (error) {
      const detail = (signal == null ? void 0 : signal.aborted) ? "The feedback request was cancelled." : "The feedback service could not be reached.";
      throw new FeedbackError({ status: 0, detail, cause: error });
    }
    const text = await response.text().catch(() => "");
    const parsed = text ? parseJson(text) : { ok: true, value: null };
    if (!response.ok) throw errorFromResponse(response.status, parsed.ok ? parsed.value : text);
    if (!parsed.ok) {
      throw new FeedbackError({ status: response.status, detail: "The feedback service did not answer with JSON." });
    }
    return parsed.value;
  };
}

// src/core/client.ts
var FeedbackClient = class {
  constructor({ fetcher, baseUrl = "/api/feedback" } = {}) {
    this.fetcher = fetcher != null ? fetcher : createFetchFetcher();
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }
  getPrompt(options) {
    return this.request("GET", "prompt/", void 0, options);
  }
  /** A 409 coded `settled` or `closed` means there is nothing left to settle. */
  submit(submission, options) {
    return this.request("POST", "responses/", submission, options);
  }
  async request(method, path, body, options) {
    try {
      const result = await this.fetcher({
        url: `${this.baseUrl}/${path}`,
        method,
        ...body === void 0 ? {} : { body },
        ...(options == null ? void 0 : options.signal) ? { signal: options.signal } : {}
      });
      return result;
    } catch (error) {
      throw normalizeError(error);
    }
  }
};

export {
  readCsrfCookie,
  createFetchFetcher,
  FeedbackClient
};
